import { useCallback, useEffect, useState } from 'react';
import QRCode from "react-qr-code";

import { getServiceSignaturesByUniqueIdentifier } from '../../core/_requests';
import { APIError, useAxios } from '../../core/_axios';
import { Service, ServiceSignature } from '../../core/_models';
import LoadingSpinner from '../../components/LoadingSpinner';
import usePageTitle from '../../hooks/usePageTitle';
import useServiceChannel from '../../hooks/useServiceChannel';
import { Link } from 'react-router-dom';

import ServiceQRCodeFrame from './ServiceQRCodeFrame';

const slugify = (text: string) => {
    return text
        .toString()
        .toLowerCase() // Convert to lowercase
        .trim() // Trim any leading or trailing whitespace
        .replace(/\s+/g, '-') // Replace spaces with hyphens
        .replace(/[^\w\-]+/g, '') // Remove all non-word characters
        .replace(/\-\-+/g, '-'); // Replace multiple hyphens with a single one
};

export default function ServiceQRCode({
    service,
}: {
    service: Service,
}) {
    usePageTitle('View Service Signatures');

    const [selectedFrame, setSelectedFrame] = useState<string>('no-frame');

    const onDownloadQRCode = useCallback(() => {
        // find inside qr-code-wrapper the canvas element
        const qrCodeSVG = document.querySelector('.qr-code-wrapper > svg');
        if (!qrCodeSVG) {
            alert("No QR code found.");
            return;
        }

        // create a new canvas element
        const canvas = document.createElement('canvas');
        const borderWidth = 100; // Adjust this for larger borders
        const size = 2048; // Canvas size
        const qrSize = size - 2 * borderWidth; // QR code size considering border

        canvas.width = size;
        canvas.height = size;

        // draw the QR code onto the canvas
        const ctx = canvas.getContext('2d');
        if (!ctx) {
            return;
        }

        // Add a white background with the border
        ctx.fillStyle = 'white';
        ctx.fillRect(0, 0, size, size);

        const img = new Image();
        img.src = `data:image/svg+xml;base64,${btoa(qrCodeSVG.outerHTML)}`;
        img.onload = () => {
            // Draw the QR code in the center with the border around it
            ctx.drawImage(img, borderWidth, borderWidth, qrSize, qrSize);

            const a = document.createElement('a');
            // a.download = 'qr-code.png';
            const slugifiedUserName = `${slugify(`${service.public_data.deceased_person.full_name || 'deceased'}`)}.png`
            a.download = slugifiedUserName
            a.href = canvas.toDataURL('image/png');
            a.click();
        };
    }, [service.public_data.deceased_person.full_name]);

    return <div className="grid grid-cols-1 xl:grid-cols-3 gap-5 lg:gap-7.5">
        <div className="col-span-2">
            <div className="flex flex-col gap-5 lg:gap-7.5">
                <div className="card" >
                    <div className="card-header">
                        <h3 className="card-title">
                            Service QR Code
                        </h3>
                    </div>
                    <div className="card-body grid gap-7.5 py-5 lg:py-7.5">
                        <div>
                            <ServiceQRCodeFrame
                                service={service}
                            />
                        </div>
                        <div>
                            <label className="block mb-2 text-sm font-medium text-gray-900 dark:text-white">
                                Print the following QR code to sign the service digitally.
                            </label>
                            <div>
                                <div className="qr-code-wrapper" style={{ background: 'white', padding: '16px' }}>
                                    <QRCode
                                        value={service.getDirectLink()}
                                        size={256}
                                    />
                                </div>
                            </div>
                            <div>
                                <button
                                    onClick={onDownloadQRCode}
                                    type="button" className="btn btn-light me-3"
                                >
                                    Download QR Code
                                </button>
                                <Link to={service.getDirectLink()}
                                    target="_blank" rel="noreferrer"
                                    className="btn btn-light"
                                >
                                    Direct Link
                                </Link>
                            </div>
                        </div>
                        {false && <div>
                            <div className="grid grid-cols-2 gap-5">
                                {[{
                                    id: 'no-frame',
                                    title: 'No Frame'
                                }, {
                                    id: 'dove',
                                    title: 'Dove',
                                }].map((item, index) => {
                                    return <div
                                        key={index}
                                        className={`bg-gray-200 dark:bg-gray-800 rounded-lg p-5 h-[100px] flex items-center justify-center cursor-pointer ${selectedFrame === item.id ? 'border-2 border-blue-500' : ''}`}
                                        onClick={() => {
                                            setSelectedFrame(item.id)
                                        }}
                                    >
                                        <span className="text-gray-700 dark:text-gray-300">
                                            {item.title}
                                        </span>
                                    </div>
                                })}
                            </div>
                        </div>}
                    </div>
                </div>
            </div>
        </div>
    </div>
}